import axios from "@galaxy/modules/Core/resources/js/axiosBuilder";
import getDatabaseColors from "../../../../../Canvas/resources/components/canvas/blocks/utils/text/admin/getDatabaseColors";
import { meetsContrast, contrastRatio } from "@galaxy/modules/Canvas/resources/js/editor/website/editors/inputs/helpers/contrast";

common.View.create('crud::form.inputs.Colorpicker', {

    onDOMLoad()
    {
        this.initColorPicker();
        this.initContrastValidation();
    },

    async initColorPicker() {
        // Simple example, see optional options for more configuration.
        const pickr = Pickr.create({
            el: this.element.find('input[type="text"]').get(0),
            theme: 'classic', // or 'monolith', or 'nano'

            useAsButton: false,

            default: this.element.find('input[type="text"]').data('default-color'),

            swatches: await this.initDatabaseColors(),

            lockOpacity: true,

            components: {

                // Main components
                opacity: false,
                hue: true,

                // Input / output Options
                interaction: {
                    hex: true,
                    input: true,
                    clear: true,
                    save: true
                }
            },

            strings: {
                save: 'Opslaan',  // Default for save button
                clear: 'Legen', // Default for clear button
                cancel: 'Annuleren' // Default for cancel button
            },

        }).on('save', (color, instance) => {
            // Update hidden field.
            const hex = color ? color.toHEXA().toString() : '';
            this.element.find('input[type="hidden"]').val(hex);

            // Re-validate the contrast pair after the value lands.
            this.validateContrastPair();

            // Hide picker on save
            pickr.hide();
        }).on('clear', (color, instance) => {
            // Set color null on clear
            this.element.find('input[type="hidden"]').val('');

            this.validateContrastPair();

            // Hide picker on clear
            pickr.hide();
        });

        this.pickr = pickr;
    },

    async initDatabaseColors()
    {
        // Grab database colors
        let databaseColors = (await getDatabaseColors()).data;

        // Initialize empty array
        let colorsToColorPicker = [];

        // Add colors from database to array in CKeditor format
        $.each(databaseColors, function (item, value)
        {
            if (typeof value !== 'string') {
                colorsToColorPicker.push(...Object.values(value));
            } else {
                colorsToColorPicker.push(value);
            }
        });

        // Filter duplicates
        let colors = [...new Set(colorsToColorPicker)];

        // Return colors
        return [
            '#ffffff',
            '#000000',
            ...colors,
        ];
    },

    /*
     * Contrast pair validation
     * ------------------------------------------------------------------
     * Theme settings has paired pickers — `primary` ↔ `primary-inverse`,
     * `secondary` ↔ `secondary-inverse`, `tertiary` ↔ `tertiary-inverse`.
     * The two colours in each pair are meant to live on top of each
     * other (e.g. a button uses primary as bg, primary-inverse as text);
     * if their AA contrast is below 4.5:1, the resulting UI is unreadable.
     *
     * Wire-up: each picker's hidden input is named `settings[color][<key>]`.
     * On any change, we look for a sibling input named
     * `settings[color][<key>-inverse]` (or strip `-inverse` in the
     * reverse direction). If both have values and contrast fails, an
     * inline error is shown under THIS picker. Cleared on the next
     * passing change. Only fires when `window.galaxyWcag` is true.
     */
    initContrastValidation() {
        if (typeof window === 'undefined' || !window.galaxyWcag) return;

        // Re-run validation on first paint (covers cases where the pair
        // is ALREADY mismatched on page load — usually fine because
        // existing settings have valid pairs, but worth catching).
        $(() => this.validateContrastPair());

        // Block submission of the surrounding form while any contrast
        // error is on screen, so editors can't accidentally save an
        // unreadable theme. Hooked once per page (the same handler
        // covers all colorpickers in the form). Idempotent via a flag.
        const $form = this.element.closest('form');
        if ($form.length && !$form.data('contrastSubmitGuard')) {
            $form.data('contrastSubmitGuard', true);
            $form.on('submit', (e) => {
                if (typeof window === 'undefined' || !window.galaxyWcag) return;
                const $errors = $form.find('.contrast-pair-error');
                if ($errors.length === 0) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                // Surface the first offending pair so the editor sees it.
                $errors.first()[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (window.toasted && typeof window.toasted.show === 'function') {
                    window.toasted.show('Kan niet opslaan: één of meer kleur-paren halen geen WCAG-contrast.', {
                        duration: 4000,
                        type: 'error',
                    });
                }
            });
        }
    },

    validateContrastPair() {
        if (typeof window === 'undefined' || !window.galaxyWcag) return;

        const $hidden = this.element.find('input[type="hidden"]');
        const myName = $hidden.attr('name') || '';
        const partnerName = this.partnerNameFor(myName);
        if (!partnerName) {
            this.clearPairError(myName);
            return;
        }

        const $partner = $(`input[type="hidden"][name="${partnerName.replace(/]/g, '\\]').replace(/\[/g, '\\[')}"]`);
        if (!$partner.length) {
            this.clearPairError(myName);
            return;
        }

        const myColor = $hidden.val();
        const partnerColor = $partner.val();
        if (!myColor || !partnerColor) {
            this.clearPairError(myName);
            return;
        }

        if (meetsContrast(myColor, partnerColor, 'AA')) {
            this.clearPairError(myName);
            return;
        }

        // Plain-language message — the WCAG ratio is meaningful to a few
        // people; "tertiaire kleur en omgekeerde kleur passen niet bij
        // elkaar" is meaningful to everyone. Show a single error per
        // pair, anchored to the inverse picker's row so it spans the
        // full form width below both swatches.
        this.showPairError(myName);
    },

    /*
     * Resolve the paired field name for a given hidden-input name.
     *   primary             → primary-inverse
     *   primary-inverse     → primary
     *   secondary           → secondary-inverse
     *   secondary-inverse   → secondary
     *   …
     * Returns null when the name doesn't fit the pattern (no pairing).
     */
    partnerNameFor(name) {
        // Match `settings[color][<key>]` or `settings[<group>][<key>]`.
        const m = name.match(/^(.+\[)([^\]]+)(\])$/);
        if (!m) return null;
        const [, prefix, key, suffix] = m;
        const baseSet = new Set(['primary', 'secondary', 'tertiary']);
        if (key.endsWith('-inverse')) {
            const base = key.slice(0, -'-inverse'.length);
            if (baseSet.has(base)) return `${prefix}${base}${suffix}`;
        } else if (baseSet.has(key)) {
            return `${prefix}${key}-inverse${suffix}`;
        }
        return null;
    },

    /*
     * Pair-level error rendering
     * ------------------------------------------------------------------
     * One error per pair. Anchored as a sibling AFTER the inverse
     * picker's `.itemWrapper.w-1/2`, with `w-full` so it forces a flex
     * line-break and spans the full row below both swatches. The next
     * pair (or "Meer kleuren") drops below the message with `mb-6`
     * worth of breathing room.
     *
     * Both `validateContrastPair()` calls (from the primary AND the
     * inverse picker) flow through `showPairError`/`clearPairError`,
     * which dedupe via the shared `data-pair-key` attribute on the
     * error element — so the message appears exactly once per pair
     * regardless of which side fired the validation.
     */
    showPairError(myName) {
        const baseKey = this.baseKeyFor(myName);
        if (!baseKey) return;

        const inverseInputName = this.namePartFor(myName, `${baseKey}-inverse`);
        const $inverseHidden = $(`input[type="hidden"][name="${inverseInputName.replace(/]/g, '\\]').replace(/\[/g, '\\[')}"]`);
        if (!$inverseHidden.length) return;

        const $inverseHalf = $inverseHidden.closest('.itemWrapper.w-1\\/2');
        if (!$inverseHalf.length) return;

        // Reuse an existing pair-error sibling if one is already there.
        let $err = $inverseHalf.next(`.contrast-pair-error[data-pair-key="${baseKey}"]`);
        if (!$err.length) {
            $err = $(
                `<p class="contrast-pair-error w-full basis-full mt-2 mb-6 text-sm text-red-600 flex items-start gap-2" data-pair-key="${baseKey}">` +
                    `<i class="fas fa-exclamation-triangle mt-0.5 shrink-0" aria-hidden="true"></i>` +
                    `<span></span>` +
                `</p>`
            );
            $inverseHalf.after($err);
        }
        $err.find('span').text(
            `${this.pairLabel(baseKey)} en omgekeerde kleur passen niet bij elkaar — kies een combinatie met meer contrast.`
        );
    },

    clearPairError(myName) {
        const baseKey = this.baseKeyFor(myName);
        if (!baseKey) return;
        // The pair error lives on the form (anchored after the inverse
        // picker's half), so a global lookup is correct here — both sides
        // of the pair clear the same node.
        $(`.contrast-pair-error[data-pair-key="${baseKey}"]`).remove();
    },

    /* Returns the base key (`primary` / `secondary` / `tertiary`) that
       owns either side of the pair containing `name`. */
    baseKeyFor(name) {
        const m = (name || '').match(/^(.+\[)([^\]]+)(\])$/);
        if (!m) return null;
        const key = m[2];
        const baseSet = new Set(['primary', 'secondary', 'tertiary']);
        if (baseSet.has(key)) return key;
        if (key.endsWith('-inverse') && baseSet.has(key.slice(0, -'-inverse'.length))) {
            return key.slice(0, -'-inverse'.length);
        }
        return null;
    },

    /* Build a sibling input name by swapping the key segment.
       e.g. namePartFor('settings[color][primary]', 'primary-inverse')
            → 'settings[color][primary-inverse]' */
    namePartFor(name, newKey) {
        const m = name.match(/^(.+\[)([^\]]+)(\])$/);
        if (!m) return null;
        return `${m[1]}${newKey}${m[3]}`;
    },

    pairLabel(baseKey) {
        return ({
            primary:   'Primaire kleur',
            secondary: 'Secundaire kleur',
            tertiary:  'Tertiaire kleur',
        })[baseKey] || 'Kleur';
    },

});
